<script setup lang="ts">
import { capitalize, startCase } from 'lodash'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { rails_url } from '@/modules/shared/utils/rails'
import { VTabList } from '@/modules/shared/components'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { useOutstandingStore } from '@/modules/dashboard/stores/outstanding-store'

const { t } = useI18n()
const route = useRoute()

const id = route.params.individual_id
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const outstandingStore = useOutstandingStore()

const individualURL = computed(() => `${rails_url()}/investors/${id}`)
const isCurrent = (name: string | string[]) => {
  if (Array.isArray(name)) {
    return name.includes(route.name as string)
  }

  return route.name === name
}

const outstanding = computed(() => outstandingStore.outstanding)

const taxYear = new Date().getFullYear() - 1

onMounted(async () => {
  await Promise.all([workspaceStore.fetchCurrent(), outstandingStore.fetchOutstandings(`individual:${id}`)])
})
</script>

<template>
  <div class="mb-12 px-4 sm:px-0">
    <VTabList
      :tabs="[
        {
          current: isCurrent('investing.individual-overview'),
          name: capitalize(t('shared.overview')),
          to: {
            name: 'investing.individual-overview',
            params: {
              individual_id: id,
            },
          },
        },
        {
          current: isCurrent('investing.individual.portfolio'),
          name: capitalize(t('shared.portfolio', 0)),
          to: {
            name: 'investing.individual.portfolio',
            params: {
              individual_id: id,
            },
          },
        },
        {
          current: isCurrent('investing.individual-invoices'),
          name: capitalize(t('shared.invoice', 0)),
          to: {
            name: 'investing.individual-invoices',
            params: {
              individual_id: id,
            },
          },
          count: outstanding.invoices_count,
        },
        {
          current: isCurrent('investing.individual.documents'),
          name: t('shared.eSignature', 0),
          to: {
            name: 'investing.individual.documents',
            params: {
              individual_id: id,
            },
          },
          count: outstanding.documents_count,
        },
        {
          current: isCurrent('filing.individual.files-system'),
          name: capitalize(t('shared.file', 0)),
          to: {
            name: 'filing.individual.files-system',
            params: {
              individual_id: id,
            },
          },
        },
        {
          current: isCurrent('investing.individual.taxes'),
          name: capitalize(t('shared.tax', 0)),
          visible: workspaceStore.current?.has_managed_entities,
          to: { name: 'investing.individual.taxes', query: { year: taxYear } },
        },
        {
          current: isCurrent([
            'investing.individual.account',
            'investing.individual.disbursement-preferences',
            'investing.individual.investment-preferences',
            'investing.individual.accreditation',
            'investing.individual.contacts',
            'investing.individual.beneficiaries',
            'investing.individual.login-information',
          ]),
          name: capitalize(t('shared.account')),
          to: {
            name: 'investing.individual.account',
            params: {
              individual_id: id,
            },
          },
        },
        {
          current: isCurrent('investing.individual.notes'),
          name: capitalize(t('shared.note', 0)),
          visible: authStore.is_site_or_group_admin,
          to: {
            name: 'investing.individual.notes',
            params: {
              individual_id: id,
            },
          },
        },
        {
          current: isCurrent('investing.individual-files'),
          name: 'Files (legacy)',
          to: `${individualURL}/files?legacy=true`,
        },
      ]"
    />
  </div>
</template>
